import { useMemo } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  Position,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Group, Stack, Text } from '@mantine/core';
import {
  Crosshair,
  Globe,
  Network,
  Server,
  ShieldAlert,
  Skull,
  Terminal,
} from 'lucide-react';
import { Pill } from '../ui/Pill';
import { colors } from '../../theme/tokens';
import type {
  ScenarioDetail,
  ScenarioNetworkSegment,
  ScenarioPhase,
  ScenarioVm,
} from '../../api/types';

/**
 * BlueprintCanvas — ReactFlow visualisation of a scenario blueprint.
 *
 * Layout:
 *   - One subflow column per network segment (attacker / internal / mgmt / dmz).
 *   - VM nodes stack vertically inside their segment column.
 *   - Attack phase edges connect attacker VMs → target VMs labelled by phase
 *     id, coloured by primary MITRE technique tactic.
 *
 * Pure presentational; data comes straight from the catalog Lambda's
 * ScenarioDetail payload (services/catalog/handler.cmd_get_scenario).
 */

const SEGMENT_ORDER: Record<string, number> = {
  attacker: 0,
  'attacker-net': 0,
  internet: 0,
  external: 1,
  mgmt: 2,
  edge: 2,
  dmz: 3,
  internal: 4,
  defender: 4,
  infra: 4,
  trainee: 5,
};

const ROLE_ICON: Record<string, typeof Server> = {
  'caldera-attacker': Skull,
  'opnsense-fw': Network,
  'linux-router': Network,
  'wazuh-indexer': ShieldAlert,
  'wazuh-manager': ShieldAlert,
  'elastic-stack': ShieldAlert,
  'web-defacement-app': Globe,
  'vulnerable-webapp': Globe,
  'windows-workstation': Terminal,
  'windows-dc': Server,
  'base-ubuntu-22': Server,
  'mail-relay': Server,
};

const SEGMENT_COLOUR: Record<string, string> = {
  attacker: '#3a1a1a',
  'attacker-net': '#3a1a1a',
  internet: '#3a1a1a',
  external: '#2a1f3a',
  mgmt: '#1f1f2b',
  edge: '#1f1f2b',
  dmz: '#1f2b2b',
  internal: '#13202b',
  defender: '#13202b',
  infra: '#13202b',
  trainee: '#1c1f2b',
};

const NODE_WIDTH = 220;
const NODE_HEIGHT = 76;
const SEGMENT_WIDTH = NODE_WIDTH + 60;
const NODE_VERTICAL_GAP = 24;
const SEGMENT_HEADER = 56;

interface VmNodeData {
  vm: ScenarioVm;
  Icon: typeof Server;
}

function VmNode({ data }: NodeProps<VmNodeData>) {
  const { vm, Icon } = data;
  return (
    <div
      style={{
        background: colors.card,
        border: `1px solid ${'#2a2a2d'}`,
        borderRadius: 8,
        padding: '10px 12px',
        width: NODE_WIDTH,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: colors.hudCyan }} />
      <Stack gap={4}>
        <Group gap={6} justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <Icon size={14} color={colors.hudCyan} />
            <Text size="xs" c="white" fw={700}>
              {vm.hostname}
              {vm.count && vm.count > 1 ? ` ×${vm.count}` : ''}
            </Text>
          </Group>
        </Group>
        <Group gap={4}>
          <Pill size="xs" variant="neutral">
            {vm.role}
          </Pill>
        </Group>
        <Text size="xs" c="dimmed" ff="JetBrains Mono">
          {vm.instanceType} · {vm.diskGb}GB
        </Text>
      </Stack>
      <Handle type="source" position={Position.Right} style={{ background: colors.hudCyan }} />
    </div>
  );
}

const nodeTypes = { vm: VmNode };

export interface BlueprintCanvasProps {
  detail: ScenarioDetail;
  /** Optional: cap rendered height; defaults to 520. */
  height?: number;
}

export function BlueprintCanvas({ detail, height = 520 }: BlueprintCanvasProps) {
  const { nodes, edges } = useMemo(() => {
    return buildGraph(detail.vms ?? [], detail.phases ?? [], detail.segments ?? []);
  }, [detail.vms, detail.phases, detail.segments]);

  return (
    <Box
      style={{
        height,
        background: colors.deep,
        border: `1px solid ${'#2a2a2d'}`,
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        proOptions={{ hideAttribution: true }}
        nodesDraggable
        nodesConnectable={false}
        edgesFocusable={false}
        zoomOnScroll
        panOnDrag
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#1f1f23" />
        <Controls position="bottom-right" showInteractive={false} />
      </ReactFlow>
    </Box>
  );
}

interface BuiltGraph {
  nodes: Node[];
  edges: Edge[];
}

function buildGraph(
  vms: ReadonlyArray<ScenarioVm>,
  phases: ReadonlyArray<ScenarioPhase>,
  declaredSegments: ReadonlyArray<ScenarioNetworkSegment>,
): BuiltGraph {
  // Resolve segments: declared first; fall back to whatever the VMs use.
  const segmentNames: string[] = [];
  const seen = new Set<string>();
  const pushSeg = (name: string | undefined) => {
    if (!name || seen.has(name)) return;
    seen.add(name);
    segmentNames.push(name);
  };
  declaredSegments.forEach((s) => pushSeg(s.name));
  vms.forEach((v) => pushSeg(v.networkSegment));
  if (segmentNames.length === 0) pushSeg('default');

  // Order columns left-to-right: attacker → mgmt → dmz → internal → trainee.
  segmentNames.sort(
    (a, b) =>
      (SEGMENT_ORDER[a] ?? 99) - (SEGMENT_ORDER[b] ?? 99) || a.localeCompare(b),
  );

  // Group VMs by segment.
  const bySeg = new Map<string, ScenarioVm[]>();
  for (const seg of segmentNames) bySeg.set(seg, []);
  for (const vm of vms) {
    const seg = vm.networkSegment || 'default';
    if (!bySeg.has(seg)) {
      bySeg.set(seg, []);
      segmentNames.push(seg);
    }
    bySeg.get(seg)!.push(vm);
  }

  const nodes: Node[] = [];
  const hostnameByName = new Map<string, ScenarioVm>();
  for (const vm of vms) hostnameByName.set(vm.hostname, vm);

  // Group / segment header nodes + child VM nodes.
  segmentNames.forEach((seg, columnIdx) => {
    const vmsInSeg = bySeg.get(seg) || [];
    const colHeight =
      SEGMENT_HEADER + Math.max(vmsInSeg.length, 1) * (NODE_HEIGHT + NODE_VERTICAL_GAP);
    nodes.push({
      id: `seg:${seg}`,
      type: 'group',
      position: { x: columnIdx * (SEGMENT_WIDTH + 40), y: 0 },
      data: {},
      style: {
        width: SEGMENT_WIDTH,
        height: colHeight,
        background: SEGMENT_COLOUR[seg] ?? '#15151a',
        border: `1px dashed ${'#2a2a2d'}`,
        borderRadius: 10,
        padding: 0,
      },
    });
    // Header label inside the group.
    nodes.push({
      id: `seg-label:${seg}`,
      parentNode: `seg:${seg}`,
      position: { x: 14, y: 12 },
      data: { label: seg.toUpperCase() },
      draggable: false,
      selectable: false,
      style: {
        background: 'transparent',
        border: 'none',
        color: colors.hudCyan,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 11,
        letterSpacing: '0.12em',
        padding: 0,
      },
    });
    vmsInSeg.forEach((vm, rowIdx) => {
      const Icon = ROLE_ICON[vm.role] ?? Server;
      nodes.push({
        id: `vm:${vm.hostname}`,
        type: 'vm',
        parentNode: `seg:${seg}`,
        extent: 'parent',
        position: {
          x: (SEGMENT_WIDTH - NODE_WIDTH) / 2,
          y: SEGMENT_HEADER + rowIdx * (NODE_HEIGHT + NODE_VERTICAL_GAP),
        },
        data: { vm, Icon },
      });
    });
  });

  // Attack phase edges: source = phase's actions[].target_vms, drawn from the
  // attacker box (or first attacker-segment VM) to each target.
  const attackerVm = vms.find(
    (v) => (v.networkSegment === 'attacker' || v.role === 'caldera-attacker'),
  );
  const edges: Edge[] = [];
  phases.forEach((phase, idx) => {
    const colour = PHASE_COLOURS[idx % PHASE_COLOURS.length];
    const sources = attackerVm ? [attackerVm.hostname] : [];
    phase.targetVms.forEach((target) => {
      sources.forEach((src) => {
        if (src === target) return;
        edges.push({
          id: `phase:${phase.id}:${src}->${target}`,
          source: `vm:${src}`,
          target: `vm:${target}`,
          label: `${idx + 1}. ${phase.name}`,
          animated: true,
          style: { stroke: colour, strokeWidth: 1.5 },
          labelStyle: {
            fill: colour,
            fontSize: 10,
          },
          labelBgStyle: { fill: '#0a0a0b' },
          markerEnd: { type: MarkerType.ArrowClosed, color: colour ?? '#22d3ee' },
        });
      });
    });
  });

  return { nodes, edges };
}

const PHASE_COLOURS = [
  '#22d3ee',
  '#a78bfa',
  '#f472b6',
  '#fbbf24',
  '#f87171',
  '#34d399',
  '#fb923c',
];

export const PHASE_NUMBER_COLOUR = (idx: number) =>
  PHASE_COLOURS[idx % PHASE_COLOURS.length] ?? '#22d3ee';

export interface PhaseBadgeProps {
  index: number;
  label: string;
  technique?: string;
}

export function PhaseBadge({ index, label, technique }: PhaseBadgeProps) {
  const colour = PHASE_NUMBER_COLOUR(index);
  return (
    <Group gap={6} wrap="nowrap" align="center">
      <span
        style={{
          background: colour,
          color: '#000',
          fontWeight: 700,
          width: 22,
          height: 22,
          borderRadius: 11,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11,
        }}
      >
        {index + 1}
      </span>
      <Text size="sm" fw={600}>
        {label}
      </Text>
      {technique ? (
        <Pill size="xs" variant="neutral">
          <Crosshair size={10} style={{ marginRight: 4 }} />
          {technique}
        </Pill>
      ) : null}
    </Group>
  );
}
